/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { TicketModuleHeader } from '../../../shared/ticket/TicketModuleHeader'
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import { spacing } from '../../../shared/theme/spacing';
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { formatRelativeDayTime } from '@shared/vueltoDateTime';
import type { CashVm } from './cashManagement.types';
import { cashArqueoCopy } from './cashArqueoCopy';
import { cashStyles } from './cashManagement.styles';
import { CashTurnoKpiRow } from './CashTurnoKpiRow';

type Props = {
  vm: CashVm;
  onOpenCloseCash: () => void;
  onRegisterOutflow: () => void;
  onRegisterInflow?: () => void;
  /** Desktop cerrada: abre el modal de apertura (proto 1b, opción CTA+modal). */
  onOpenCash?: () => void;
};

/** Encabezado record page: TicketModuleHeader Square + KPI strip soft. */
export function CashRecordHeader({ vm, onOpenCloseCash, onRegisterOutflow, onRegisterInflow, onOpenCash }: Props) {
  const { layoutWebShell } = useContentLayout('dataWide');
  const activeSession = vm.cashSummary?.activeSession;
  const readOnly = vm.canView && !vm.canEdit;

  const recordTitle = activeSession ? 'Turno de caja' : 'Caja cerrada';
  const recordMeta = useMemo(() => {
    if (!activeSession) {
      return vm.branchName ? `${vm.branchName} · Sin turno abierto` : 'Sin turno abierto';
    }
    const parts = [
      vm.branchName ?? null,
      vm.turnOpenDurationLabel ?? `Apertura ${formatRelativeDayTime(activeSession.openedAt)}`,
      vm.activeSessionOpenerLabel ? `Por ${vm.activeSessionOpenerLabel}` : null,
    ].filter(Boolean);
    return parts.join(' · ');
  }, [activeSession, vm.activeSessionOpenerLabel, vm.turnOpenDurationLabel, vm.branchName]);

  const statusLabel = activeSession ? cashArqueoCopy.statusOpen : cashArqueoCopy.statusClosed;
  const statusPill = (
    <View style={{ alignSelf: 'center' }}>
      <StatusPill label={statusLabel} tone={activeSession ? 'success' : 'neutral'} compact />
    </View>
  );

  // ── Trailing CTA (desktop): abierto → Ingreso/Egreso/Cerrar; cerrado → Abrir caja ──
  const trailing = layoutWebShell && !activeSession && onOpenCash ? (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
      {statusPill}
      <TouchableOpacity
        style={[cashStyles.deskActionTileGold, (!vm.canEdit || vm.openingCash) && { opacity: 0.45 }]}
        onPress={onOpenCash}
        disabled={!vm.canEdit || vm.openingCash}
        accessibilityRole="button"
        accessibilityLabel="Abrir caja"
        testID="caja.open.cta"
      >
        {vm.openingCash ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <Text style={cashStyles.deskActionTileGoldText}>Abrir caja</Text>
        )}
      </TouchableOpacity>
    </View>
  ) : layoutWebShell && activeSession ? (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, justifyContent: 'flex-end', alignItems: 'center' }}>
      {statusPill}
      {activeSession && vm.cashOutflowsEnabled && onRegisterInflow ? (
        <TouchableOpacity
          style={[cashStyles.deskActionTile, !vm.canEdit && { opacity: 0.45 }]}
          onPress={onRegisterInflow}
          disabled={!vm.canEdit}
          accessibilityRole="button"
          accessibilityLabel={cashArqueoCopy.registerInflowCta}
          testID="caja.inflow.open"
        >
          <Text style={cashStyles.deskActionTileText}>{cashArqueoCopy.registerInflowCta}</Text>
        </TouchableOpacity>
      ) : null}
      {activeSession && vm.cashOutflowsEnabled ? (
        <TouchableOpacity
          style={[cashStyles.deskActionTile, !vm.canEdit && { opacity: 0.45 }]}
          onPress={onRegisterOutflow}
          disabled={!vm.canEdit}
          accessibilityRole="button"
          accessibilityLabel={cashArqueoCopy.registerOutflowCta}
          testID="caja.outflow.open"
        >
          <Text style={cashStyles.deskActionTileText}>{cashArqueoCopy.registerOutflowCta}</Text>
        </TouchableOpacity>
      ) : null}
      <TouchableOpacity
        style={[cashStyles.deskActionTileGold, (!vm.canEdit || vm.closingCash) && { opacity: 0.45 }]}
        onPress={onOpenCloseCash}
        disabled={!vm.canEdit || vm.closingCash}
        accessibilityRole="button"
        accessibilityLabel={cashArqueoCopy.closeTurnoCta}
        testID="caja.close.submit"
      >
        {vm.closingCash ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <Text style={cashStyles.deskActionTileGoldText}>{cashArqueoCopy.closeTurnoCta}</Text>
        )}
      </TouchableOpacity>
    </View>
  ) : null;

  // Nota: los CTAs mobile (Registrar ingreso / Registrar egreso / Contar y
  // cerrar) los provee CashTurnoHeroPanel en el cuerpo del turno, después del
  // desglose de efectivo (flujo intencional). No duplicarlos acá para evitar
  // mostrarlos dos veces.

  return (
    <View style={{ gap: 10 }} accessibilityRole="summary">
      <TicketModuleHeader
        kicker="Caja"
        title={recordTitle}
        meta={recordMeta}
        trailing={trailing}
        testID="caja.header.sheet"
      >
        {readOnly ? (
          <Text style={cashStyles.readOnlyStripHint}>
            Solo lectura — pedí permiso de edición para abrir o cerrar caja.
          </Text>
        ) : null}
      </TicketModuleHeader>

      {/* Desktop: las métricas viven en el contenido del tab (hero + KPI grid). */}
      {!layoutWebShell ? <CashTurnoKpiRow vm={vm} /> : null}
    </View>
  );
}
